/**
 * IntelliGems A/B Test: Shipping + Luggage Tag progress with Sticky ATC
 *
 * Controlled via BODY attribute:
 *   Control (A): document.body.setAttribute('data-sticky-atc-progress', 'false')
 *   Variant (B): document.body.setAttribute('data-sticky-atc-progress', 'true')
 *
 * Dev preview: ?sticky_atc_progress_test=b
 *
 * Desktop: floating card above sticky ATC (aligned to ATC button).
 * Mobile: progress block under the ATC button inside the sticky bar.
 */
(function () {
  'use strict';

  if (window.__stickyAtcProgressABTestInit) return;
  window.__stickyAtcProgressABTestInit = true;

  var BODY_ATTR = 'data-sticky-atc-progress';
  var ROOT_ATTR = 'data-sticky-atc-progress-root';
  var SLOT_ID = 'sticky-atc-progress-slot';
  var applying = false;
  var debounceTimer = null;
  var positionTimer = null;
  var pollTimer = null;
  var pollAttempts = 0;
  var variantActive = null;
  var config = null;
  var widget = null;
  var cartCache = null;
  var cartFetch = null;
  var barObserver = null;
  var resizeObserver = null;

  function isDevVariantB() {
    try {
      return new URLSearchParams(window.location.search).get('sticky_atc_progress_test') === 'b';
    } catch (e) {
      return false;
    }
  }

  function isTruthy(value) {
    if (value == null) return false;
    var normalized = String(value).trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on';
  }

  function isVariantB() {
    if (isDevVariantB()) return true;
    var body = document.body;
    var html = document.documentElement;
    if (!body || !html) return false;
    return isTruthy(body.getAttribute(BODY_ATTR)) || isTruthy(html.getAttribute(BODY_ATTR));
  }

  function isDesktop() {
    return window.matchMedia('(min-width: 769px)').matches;
  }

  function readConfig() {
    if (config) return config;

    var node = document.getElementById('sticky-atc-progress-ab-data');
    if (!node) return null;

    try {
      config = JSON.parse(node.textContent);
    } catch (e) {
      config = null;
    }

    return config;
  }

  function formatMoney(cents, moneyFormat) {
    var amount = (Math.max(0, cents) / 100).toFixed(2);
    var amountWithComma = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var noDecimals = amountWithComma.split('.')[0];
    var format = moneyFormat || '${{amount}}';

    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountWithComma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, amountWithComma);
  }

  function findStickyBar() {
    return document.getElementById('custom-sticky-atc');
  }

  function findProgressSlot(bar) {
    if (!bar) return null;
    return bar.querySelector('#' + SLOT_ID) || document.getElementById(SLOT_ID);
  }

  function isBarVisible(bar) {
    if (!bar) return false;

    if (bar.getAttribute('data-satc-visible') === 'false') return false;
    if (bar.getAttribute('data-satc-visible') === 'true') return true;

    var style = window.getComputedStyle(bar);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
      return false;
    }

    var rect = bar.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return false;
    if (rect.bottom < 0 || rect.top > window.innerHeight + 4) return false;

    return true;
  }

  function getProgressTotalCents() {
    var cfg = readConfig() || {};

    // Only real cart total — never preview PDP price when cart is empty
    if (cartCache && typeof cartCache.total_price === 'number') {
      return cartCache.total_price;
    }

    return Number(cfg.cartTotal) || 0;
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function buildCopy(totalCents) {
    var cfg = readConfig() || {};
    var freeShip = Math.max(0, Number(cfg.freeShippingThreshold) || 0);
    var gift = Math.max(freeShip, Number(cfg.giftThreshold) || 0);
    var giftName = cfg.giftName || 'Luggage Tag';
    var moneyFormat = cfg.moneyFormat;

    var shippingEarned = freeShip <= 0 || totalCents >= freeShip;
    var giftEarned = gift <= 0 || totalCents >= gift;

    var top = '';
    var bottom = '';

    if (giftEarned) {
      top = 'You have unlocked your free <strong>' + escapeHtml(giftName) + '</strong>!';
      bottom = shippingEarned ? 'You have earned <strong>Free Shipping.</strong>' : '';
    } else if (shippingEarned) {
      var giftRemaining = Math.max(0, gift - totalCents);
      top =
        'Your <strong>' +
        escapeHtml(giftName) +
        '</strong> is only <strong>' +
        escapeHtml(formatMoney(giftRemaining, moneyFormat)) +
        '</strong> away!';
      bottom = 'You have earned <strong>Free Shipping.</strong>';
    } else {
      var shipRemaining = Math.max(0, freeShip - totalCents);
      top =
        '<strong>Free Shipping</strong> is only <strong>' +
        escapeHtml(formatMoney(shipRemaining, moneyFormat)) +
        '</strong> away!';
      bottom =
        'Unlock a free <strong>' +
        escapeHtml(giftName) +
        '</strong> at <strong>' +
        escapeHtml(formatMoney(gift, moneyFormat)) +
        '</strong>.';
    }

    var fillPercent = gift > 0 ? Math.max(0, Math.min(100, (totalCents / gift) * 100)) : 100;
    var shipPercent = gift > 0 && freeShip > 0 ? Math.max(0, Math.min(100, (freeShip / gift) * 100)) : 0;

    // Keep truck badge visually distinct when both thresholds match
    if (shipPercent >= 96) shipPercent = 78;

    return {
      top: top,
      bottom: bottom,
      fillPercent: fillPercent,
      shipPercent: shipPercent,
      shippingEarned: shippingEarned,
      giftEarned: giftEarned
    };
  }

  function ensureWidget() {
    if (widget && widget.isConnected) return widget;

    var existing = document.querySelector('[' + ROOT_ATTR + ']');
    if (existing) {
      widget = existing;
      return widget;
    }

    var template = document.getElementById('sticky-atc-progress-ab-template');
    if (template && template.content) {
      var fromTemplate = template.content.querySelector('[' + ROOT_ATTR + ']');
      if (fromTemplate) {
        widget = fromTemplate.cloneNode(true);
        document.body.appendChild(widget);
        return widget;
      }
    }

    return null;
  }

  function mountWidget(bar) {
    var root = ensureWidget();
    if (!root || !bar) return root;

    if (isDesktop()) {
      if (root.parentElement !== document.body) {
        document.body.appendChild(root);
      }
      return root;
    }

    var slot = findProgressSlot(bar);
    if (slot) {
      if (root.parentElement !== slot) {
        slot.appendChild(root);
      }
    } else if (root.parentElement !== bar) {
      bar.appendChild(root);
    }

    return root;
  }

  function updateWidgetContent() {
    var root = ensureWidget();
    if (!root) return false;

    var state = buildCopy(getProgressTotalCents());
    var top = root.querySelector('[data-sticky-atc-progress-top]');
    var bottom = root.querySelector('[data-sticky-atc-progress-bottom]');
    var fill = root.querySelector('[data-sticky-atc-progress-fill]');
    var ship = root.querySelector('[data-sticky-atc-progress-ship]');
    var gift = root.querySelector('[data-sticky-atc-progress-gift]');

    if (top) top.innerHTML = state.top;
    if (bottom) {
      bottom.innerHTML = state.bottom;
      bottom.hidden = !state.bottom;
    }
    if (fill) fill.style.width = state.fillPercent.toFixed(2) + '%';

    if (ship) {
      ship.style.left = state.shipPercent.toFixed(2) + '%';
      ship.classList.toggle('sticky-atc-progress-ab__badge--earned', state.shippingEarned);
    }

    if (gift) {
      gift.classList.toggle('sticky-atc-progress-ab__badge--earned', state.giftEarned);
    }

    root.classList.toggle('sticky-atc-progress-ab--shipping-earned', state.shippingEarned);
    root.classList.toggle('sticky-atc-progress-ab--gift-earned', state.giftEarned);

    return true;
  }

  function positionWidget() {
    var bar = findStickyBar();
    var root = mountWidget(bar);

    if (!root) return;

    if (!isVariantB()) {
      root.hidden = true;
      return;
    }

    if (!isBarVisible(bar)) {
      root.hidden = true;
      return;
    }

    updateWidgetContent();
    root.hidden = false;

    if (isDesktop()) {
      var rect = bar.getBoundingClientRect();
      // Sit just above sticky ATC so bottom copy is not clipped
      var bottom = Math.max(0, window.innerHeight - rect.top + 8);

      root.style.bottom = bottom + 'px';
      root.style.position = 'fixed';

      var button = bar.querySelector('#satc-submit, .satc-btn') || null;
      var rightEdge = window.innerWidth - (button ? button.getBoundingClientRect().right : rect.right);
      rightEdge = Math.max(24, rightEdge);
      root.style.left = 'auto';
      root.style.right = rightEdge + 'px';
      root.style.width = '';
    } else {
      root.style.bottom = '';
      root.style.left = '';
      root.style.right = '';
      root.style.width = '';
      root.style.position = '';
    }
  }

  function schedulePosition() {
    if (positionTimer) clearTimeout(positionTimer);
    positionTimer = setTimeout(positionWidget, 40);
  }

  function observeStickyBar() {
    if (barObserver || !window.MutationObserver) return;

    barObserver = new MutationObserver(function () {
      if (!isVariantB()) return;
      schedulePosition();
    });

    barObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'data-satc-visible']
    });
  }

  function observeResize() {
    if (resizeObserver) return;

    window.addEventListener('scroll', schedulePosition, { passive: true });
    window.addEventListener('resize', schedulePosition);

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(schedulePosition);
      var bar = findStickyBar();
      if (bar) resizeObserver.observe(bar);
    }
  }

  function applyVariant() {
    if (applying) return;
    applying = true;

    try {
      var variantB = isVariantB();
      document.documentElement.classList.toggle('sticky-atc-progress-b', variantB);

      if (variantB === variantActive) {
        if (variantB) schedulePosition();
        else if (widget) widget.hidden = true;
        return;
      }

      variantActive = variantB;

      if (variantB) {
        observeStickyBar();
        observeResize();
        startPolling();
        schedulePosition();
        // Ask sticky ATC to re-evaluate visibility (desktop enabled for variant B)
        window.dispatchEvent(new CustomEvent('sticky-atc-progress:variant', { detail: { active: true } }));
      } else if (widget) {
        widget.hidden = true;
        window.dispatchEvent(new CustomEvent('sticky-atc-progress:variant', { detail: { active: false } }));
      }
    } finally {
      applying = false;
    }
  }

  function scheduleApply() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(applyVariant, 40);
  }

  function startPolling() {
    if (pollTimer) clearTimeout(pollTimer);
    pollAttempts = 0;

    function tick() {
      if (!isVariantB()) return;
      positionWidget();
      pollAttempts += 1;
      if (pollAttempts < 50) {
        pollTimer = setTimeout(tick, findStickyBar() && isBarVisible(findStickyBar()) ? 1200 : 250);
      }
    }

    tick();
  }

  function fetchCartThenApply() {
    if (cartFetch) return cartFetch;

    var rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var cartUrl = rootPath.replace(/\/?$/, '/') + 'cart.js';

    cartFetch = fetch(cartUrl)
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        cartCache = cart || { item_count: 0, total_price: 0, items: [] };
        scheduleApply();
      })
      .catch(function () {
        scheduleApply();
      })
      .then(function () {
        cartFetch = null;
      });

    return cartFetch;
  }

  function refreshCart() {
    cartFetch = null;
    fetchCartThenApply();
  }

  function isCartUrl(url) {
    return /\/cart\/(add|change|update|clear)/i.test(String(url || ''));
  }

  function observeRoot(root) {
    if (!root || !window.MutationObserver) return;

    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (
          mutations[i].attributeName === BODY_ATTR ||
          mutations[i].attributeName === 'data-gem-variant' ||
          mutations[i].attributeName === 'data-gem-test-active'
        ) {
          scheduleApply();
          return;
        }
      }
    }).observe(root, {
      attributes: true,
      attributeFilter: [BODY_ATTR, 'data-gem-variant', 'data-gem-test-active']
    });
  }

  function init() {
    var isProduct =
      document.getElementById('sticky-atc-progress-ab-data') ||
      document.getElementById('custom-sticky-atc') ||
      /\/products\//.test(window.location.pathname);

    if (!isProduct) return;

    if (isDevVariantB()) {
      if (document.documentElement.getAttribute(BODY_ATTR) !== 'true') {
        document.documentElement.setAttribute(BODY_ATTR, 'true');
      }
      if (document.body && document.body.getAttribute(BODY_ATTR) !== 'true') {
        document.body.setAttribute(BODY_ATTR, 'true');
      }
    }

    var cfg = readConfig();
    if (cfg) {
      cartCache = {
        item_count: Number(cfg.cartItemCount) || 0,
        total_price: Number(cfg.cartTotal) || 0,
        items: []
      };
    }

    applyVariant();
    fetchCartThenApply();

    observeRoot(document.documentElement);
    observeRoot(document.body);

    document.addEventListener('gem:variant:changed', scheduleApply);
    document.addEventListener('cart:refresh', refreshCart);
    document.addEventListener('product:added', refreshCart);
    document.addEventListener('variant:changed', schedulePosition);
    document.addEventListener('product:variant-change', schedulePosition);
    window.addEventListener('popstate', schedulePosition);

    document.addEventListener('change', function (event) {
      if (!event.target) return;
      if (event.target.matches('[name="id"], select[name="id"], variant-picker input')) {
        schedulePosition();
      }
    });

    ['cart:updated', 'cart:change', 'cart:added'].forEach(function (name) {
      document.addEventListener(name, refreshCart);
    });

    if (window.fetch) {
      var nativeFetch = window.fetch;
      window.fetch = function () {
        var input = arguments[0];
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var request = nativeFetch.apply(this, arguments);
        if (isCartUrl(url)) {
          request
            .then(function () {
              refreshCart();
            })
            .catch(function () {});
        }
        return request;
      };
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
